import React, { useState, useEffect } from "react";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import { CircleLoaderComponent } from "../../../components/Loader/CircleLoaderComponent";
import toast from "react-hot-toast";
import {
  useLazyFetchIffcoExpiryAgeingQuery,
  useLazyFetchIffcoInventorySummaryQuery,
  useLazyFetchIffcoScanActivityLogQuery,
  useLazyFetchIffcoRetailerCoverageQuery,
  useLazyFetchIffcoUnscannedQrQuery,
} from "../../../features/tenant/Iffco/IffcoApi";

const IffcoReports = () => {
  const [activeTab, setActiveTab] = useState("R2"); // R2 is Expiry Ageing

  // Filters
  const [asOnDate, setAsOnDate] = useState(new Date().toISOString().split("T")[0]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [expiryBucket, setExpiryBucket] = useState("");
  const [retailerCode, setRetailerCode] = useState("");
  const [productCode, setProductCode] = useState("");
  const [batchCode, setBatchCode] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [tableData, setTableData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);

  // RTK Query hooks
  const [triggerExpiryAgeing, { isLoading: isAgeingLoading }] = useLazyFetchIffcoExpiryAgeingQuery();
  const [triggerInventory, { isLoading: isInventoryLoading }] = useLazyFetchIffcoInventorySummaryQuery();
  const [triggerScanLog, { isLoading: isScanLogLoading }] = useLazyFetchIffcoScanActivityLogQuery();
  const [triggerCoverage, { isLoading: isCoverageLoading }] = useLazyFetchIffcoRetailerCoverageQuery();
  const [triggerUnscannedQr, { isLoading: isUnscannedLoading }] = useLazyFetchIffcoUnscannedQrQuery();

  const isLoading = isAgeingLoading || isInventoryLoading || isScanLogLoading || isCoverageLoading || isUnscannedLoading;

  const tabsConfig = [
    { id: "R2", label: "Expiry Ageing", icon: "bi-calendar-event", color: "danger", desc: "Monitor stock expiry timeline & risk buckets" },
    { id: "R1", label: "Retailer Inventory", icon: "bi-boxes", color: "primary", desc: "Summary of active retailer stock in hand" },
    { id: "R3", label: "Scan Activity Log", icon: "bi-qr-code-scan", color: "success", desc: "Real-time audit log of QR code scans" },
    { id: "R4", label: "Retailer Coverage", icon: "bi-pie-chart", color: "info", desc: "Retailer engagement & active scan distribution" },
    { id: "R5", label: "Unscanned QRs", icon: "bi-upc-scan", color: "warning", desc: "Warehouse stock QRs awaiting retailer scan" },
  ];

  const activeTabMeta = tabsConfig.find((t) => t.id === activeTab) || tabsConfig[0];

  const getParams = () => {
    const vendorData = JSON.parse(sessionStorage.getItem("vendorData") || "{}");
    const token = sessionStorage.getItem("access_token");
    return { tenant_id: vendorData.tenant_id, token };
  };

  const loadData = async () => {
    const { tenant_id, token } = getParams();
    if (!tenant_id || !token) return;

    const queryParams = {
      page: currentPage,
      limit,
      as_on_date: asOnDate,
      start_date: startDate,
      end_date: endDate,
      expiry_bucket: expiryBucket,
      retailer_code: retailerCode,
      product_code: productCode,
      batch_code: batchCode,
      state: stateFilter,
      district: districtFilter,
    };

    // Clean up empty params
    Object.keys(queryParams).forEach((key) => {
      if (!queryParams[key]) delete queryParams[key];
    });

    try {
      let res;
      if (activeTab === "R2") {
        res = await triggerExpiryAgeing({ tenant_id, token, query: queryParams }).unwrap();
      } else if (activeTab === "R1") {
        res = await triggerInventory({ tenant_id, token, query: queryParams }).unwrap();
      } else if (activeTab === "R3") {
        res = await triggerScanLog({ tenant_id, token, query: queryParams }).unwrap();
      } else if (activeTab === "R4") {
        res = await triggerCoverage({ tenant_id, token, query: queryParams }).unwrap();
      } else if (activeTab === "R5") {
        res = await triggerUnscannedQr({ tenant_id, token, query: queryParams }).unwrap();
      }

      if (res) {
        const data = res.body?.data || res.data || [];
        const total = Number(res.body?.total_count ?? res.body?.total ?? res.total_count ?? res.total ?? data.length);
        setTableData(data);
        setTotalRows(total);
      } else {
        setTableData([]);
        setTotalRows(0);
      }
    } catch (err) {
      console.error("Error loading IFFCO report data:", err);
      toast.error(err?.data?.message || "Failed to load report data");
    }
  };

  useEffect(() => {
    loadData();
  }, [activeTab, currentPage, limit]);

  const handleApplyFilter = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    loadData();
  };

  const handleResetFilter = () => {
    setAsOnDate(new Date().toISOString().split("T")[0]);
    setStartDate("");
    setEndDate("");
    setExpiryBucket("");
    setRetailerCode("");
    setProductCode("");
    setBatchCode("");
    setStateFilter("");
    setDistrictFilter("");
    setCurrentPage(1);
    setTimeout(loadData, 50);
  };

  // Define table columns dynamically per report
  const getColumns = () => {
    const offset = (currentPage - 1) * limit;

    if (activeTab === "R2") {
      return [
        { name: "S.No", cell: (row, idx) => <span className="fw-semibold text-secondary">{offset + idx + 1}</span>, width: "65px" },
        { name: "Batch Code", selector: (row) => row.batch_code || "-", sortable: true, minWidth: "130px", wrap: true },
        { name: "QR Code", selector: (row) => row.unique_code || row.qr_code || "-", sortable: true, minWidth: "140px", wrap: true },
        { name: "Product Code", selector: (row) => row.product_code || "-", sortable: true, minWidth: "130px", wrap: true },
        { name: "Product Name", selector: (row) => row.product_name || "-", sortable: true, minWidth: "160px", wrap: true },
        { name: "Mfg Date", selector: (row) => row.mfg_date ? new Date(row.mfg_date).toLocaleDateString() : "-", sortable: true, minWidth: "120px" },
        { name: "Expiry Date", selector: (row) => row.expiry_date ? new Date(row.expiry_date).toLocaleDateString() : "-", sortable: true, minWidth: "120px" },
        {
          name: "Days To Expiry",
          cell: (row) => {
            const val = row.days_to_expiry;
            if (val === null || val === undefined) return "-";
            const colorClass = val < 0 ? "text-danger fw-bold" : val <= 30 ? "text-warning fw-bold" : "text-dark";
            return <span className={colorClass}>{val} days</span>;
          },
          sortable: true,
          minWidth: "135px",
        },
        {
          name: "Expiry Bucket",
          cell: (row) => (
            <span className={`badge rounded-pill px-3 py-1 ${row.expiry_bucket === "Expired" ? "bg-danger" : row.expiry_bucket === "0-30" ? "bg-warning text-dark" : "bg-info text-white"}`}>
              {row.expiry_bucket}
            </span>
          ),
          sortable: true,
          minWidth: "130px",
        },
        { name: "Retailer Code", selector: (row) => row.retailer_code || "-", sortable: true, minWidth: "135px", wrap: true },
        { name: "Retailer Name", selector: (row) => row.retailer_name || "-", sortable: true, minWidth: "150px", wrap: true },
        { name: "State", selector: (row) => row.state || "-", sortable: true, minWidth: "120px", wrap: true },
        { name: "Code Count", selector: (row) => <span className="badge bg-light text-dark border">{row.code_count || 0}</span>, sortable: true, minWidth: "110px" },
        {
          name: "Status",
          cell: (row) => (
            <span className={`badge rounded-pill px-3 py-1 ${row.status === "Expired" ? "bg-danger" : row.status === "Near Expiry" ? "bg-warning text-dark" : "bg-success"}`}>
              {row.status}
            </span>
          ),
          minWidth: "110px",
        },
      ];
    }

    if (activeTab === "R1") {
      return [
        { name: "S.No", cell: (row, idx) => <span className="fw-semibold text-secondary">{offset + idx + 1}</span>, width: "65px" },
        { name: "Retailer Code", selector: (row) => row.retailer_code || "-", sortable: true, minWidth: "135px", wrap: true },
        { name: "Retailer Name", selector: (row) => row.retailer_name || "-", sortable: true, minWidth: "150px", wrap: true },
        { name: "Mobile", selector: (row) => row.mobile || "-", sortable: true, minWidth: "120px" },
        { name: "State", selector: (row) => row.state || "-", sortable: true, minWidth: "120px", wrap: true },
        { name: "District", selector: (row) => row.district || "-", sortable: true, minWidth: "120px", wrap: true },
        { name: "Product Code", selector: (row) => row.product_code || "-", sortable: true, minWidth: "130px", wrap: true },
        { name: "Product Name", selector: (row) => row.product_name || "-", sortable: true, minWidth: "160px", wrap: true },
        { name: "Batch Code", selector: (row) => row.batch_code || "-", sortable: true, minWidth: "130px", wrap: true },
        { name: "QR Code", selector: (row) => row.unique_code || row.qr_code || "-", sortable: true, minWidth: "140px", wrap: true },
        { name: "Codes In Hand", selector: (row) => <span className="badge bg-primary px-2.5 py-1">{row.codes_in_hand || 0}</span>, sortable: true, minWidth: "135px" },
        { name: "Scan Date", selector: (row) => row.first_scan_date ? new Date(row.first_scan_date).toLocaleString() : "-", sortable: true, minWidth: "160px" },
      ];
    }

    if (activeTab === "R3") {
      return [
        { name: "S.No", cell: (row, idx) => <span className="fw-semibold text-secondary">{offset + idx + 1}</span>, width: "65px" },
        { name: "Name", selector: (row) => row.name || "-", sortable: true, minWidth: "140px", wrap: true },
        { name: "Retailer Code", selector: (row) => row.retailer_code || "-", sortable: true, minWidth: "135px", wrap: true },
        { name: "Mobile", selector: (row) => row.mobile || "-", sortable: true, minWidth: "120px" },
        { name: "Product Name", selector: (row) => row.product_name || "-", sortable: true, minWidth: "160px", wrap: true },
        { name: "Batch Code", selector: (row) => row.batch_code || "-", sortable: true, minWidth: "130px", wrap: true },
        { name: "QR Code", selector: (row) => row.qr_code || "-", sortable: true, minWidth: "150px", wrap: true },
        { name: "Scan Type", selector: (row) => <span className="badge bg-light text-primary border">{row.scan_type || "Inward"}</span>, minWidth: "110px" },
        { name: "Scan Result", cell: (row) => <span className="badge rounded-pill bg-success px-3 py-1">{row.scan_result || "Accepted"}</span>, minWidth: "120px" },
        { name: "Scanned At", selector: (row) => row.scanned_at ? new Date(row.scanned_at).toLocaleString() : "-", sortable: true, minWidth: "160px" },
      ];
    }

    if (activeTab === "R4") {
      return [
        { name: "S.No", cell: (row, idx) => <span className="fw-semibold text-secondary">{offset + idx + 1}</span>, width: "65px" },
        { name: "Retailer Code", selector: (row) => row.retailer_code || "-", sortable: true, minWidth: "135px", wrap: true },
        { name: "Retailer Name", selector: (row) => row.retailer_name || "-", sortable: true, minWidth: "150px", wrap: true },
        { name: "State", selector: (row) => row.state || "-", sortable: true, minWidth: "120px", wrap: true },
        { name: "District", selector: (row) => row.district || "-", sortable: true, minWidth: "120px", wrap: true },
        { name: "Total Scans", selector: (row) => <span className="badge bg-info text-white">{row.total_scans_in_period || 0}</span>, sortable: true, minWidth: "120px" },
        { name: "Last Scan Date", selector: (row) => row.last_scan_date ? new Date(row.last_scan_date).toLocaleDateString() : "Never", sortable: true, minWidth: "140px" },
        { name: "Days Since Last Scan", selector: (row) => row.days_since_last_scan ?? "-", sortable: true, minWidth: "170px" },
        {
          name: "Activity Status",
          cell: (row) => (
            <span className={`badge rounded-pill px-3 py-1 ${row.activity_status === "Active" ? "bg-success" : row.activity_status === "Dormant" ? "bg-warning text-dark" : "bg-secondary"}`}>
              {row.activity_status}
            </span>
          ),
          minWidth: "130px",
        },
      ];
    }

    if (activeTab === "R5") {
      return [
        { name: "S.No", cell: (row, idx) => <span className="fw-semibold text-secondary">{offset + idx + 1}</span>, width: "65px" },
        { name: "QR Code", selector: (row) => row.qr_code || row.unique_code || "-", sortable: true, minWidth: "150px", wrap: true },
        { name: "Batch Code", selector: (row) => row.batch_code || "-", sortable: true, minWidth: "130px", wrap: true },
        { name: "Product Code", selector: (row) => row.product_code || "-", sortable: true, minWidth: "130px", wrap: true },
        { name: "Product Name", selector: (row) => row.product_name || "-", sortable: true, minWidth: "160px", wrap: true },
        { name: "Mfg Date", selector: (row) => row.mfg_date ? new Date(row.mfg_date).toLocaleDateString() : "-", sortable: true, minWidth: "120px" },
        { name: "Expiry Date", selector: (row) => row.expiry_date ? new Date(row.expiry_date).toLocaleDateString() : "-", sortable: true, minWidth: "120px" },
        {
          name: "Days To Expiry",
          cell: (row) => {
            const val = row.days_to_expiry;
            if (val === null || val === undefined) return "-";
            const colorClass = val < 0 ? "text-danger fw-bold" : val <= 30 ? "text-warning fw-bold" : "text-dark";
            return <span className={colorClass}>{val} days</span>;
          },
          sortable: true,
          minWidth: "135px",
        },
        {
          name: "Expiry Bucket",
          cell: (row) => (
            <span className={`badge rounded-pill px-3 py-1 ${row.expiry_bucket === "Expired" ? "bg-danger" : row.expiry_bucket === "0-30" ? "bg-warning text-dark" : "bg-info text-white"}`}>
              {row.expiry_bucket}
            </span>
          ),
          sortable: true,
          minWidth: "130px",
        },
        {
          name: "Status",
          cell: (row) => (
            <span className={`badge rounded-pill px-3 py-1 ${row.status === "Expired" ? "bg-danger" : row.status === "Near Expiry" ? "bg-warning text-dark" : "bg-success"}`}>
              {row.status}
            </span>
          ),
          minWidth: "110px",
        },
      ];
    }

    return [];
  };

  const customTableStyles = {
    headRow: {
      style: {
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #e9ecef",
        fontWeight: "600",
        fontSize: "13.5px",
        color: "#495057",
        minHeight: "48px",
      },
    },
    rows: {
      style: {
        fontSize: "13px",
        color: "#212529",
        minHeight: "46px",
        "&:hover": {
          backgroundColor: "#f8f9fa!important",
          transition: "all 0.15s ease-in-out",
        },
      },
    },
  };

  return (
    <div className="container-fluid py-3">
      <LinksComponent
        mainLink="IFFCO Reports"
        subLink={
          activeTab === "R2"
            ? "Expiry Ageing"
            : activeTab === "R1"
            ? "Retailer Inventory Summary"
            : activeTab === "R3"
            ? "Scan Activity Log"
            : activeTab === "R4"
            ? "Retailer Coverage"
            : "Unscanned QRs"
        }
      />

      {/* Nav Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "R2" ? "active fw-bold" : ""}`}
            onClick={() => { setActiveTab("R2"); setCurrentPage(1); }}
          >
            Expiry Ageing
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "R1" ? "active fw-bold" : ""}`}
            onClick={() => { setActiveTab("R1"); setCurrentPage(1); }}
          >
            Retailer Inventory Summary
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "R3" ? "active fw-bold" : ""}`}
            onClick={() => { setActiveTab("R3"); setCurrentPage(1); }}
          >
            Scan Activity Log
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "R4" ? "active fw-bold" : ""}`}
            onClick={() => { setActiveTab("R4"); setCurrentPage(1); }}
          >
            Retailer Coverage
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "R5" ? "active fw-bold" : ""}`}
            onClick={() => { setActiveTab("R5"); setCurrentPage(1); }}
          >
            Unscanned QRs
          </button>
        </li>
      </ul>

      {/* Filter Card Section */}
      <div className="card border shadow-sm mb-4 rounded-3">
        <div className="card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-funnel text-primary fs-6"></i>
            <h6 className="m-0 fw-bold text-dark">Filter Reports</h6>
          </div>
          <span className="badge bg-light text-secondary border font-normal">Filter Criteria</span>
        </div>
        <div className="card-body bg-light bg-opacity-25 p-3">
          <form onSubmit={handleApplyFilter} className="row g-3 align-items-end">
            {(activeTab === "R1" || activeTab === "R2" || activeTab === "R5") && (
              <div className="col-md-3 col-sm-6">
                <label className="form-label text-dark fw-medium small mb-1">As-On Date</label>
                <input
                  type="date"
                  className="form-control form-control-sm border-gray-300 rounded"
                  value={asOnDate}
                  onChange={(e) => setAsOnDate(e.target.value)}
                />
              </div>
            )}

            {(activeTab === "R3" || activeTab === "R4") && (
              <>
                <div className="col-md-3 col-sm-6">
                  <label className="form-label text-dark fw-medium small mb-1">Start Date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm border-gray-300 rounded"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="col-md-3 col-sm-6">
                  <label className="form-label text-dark fw-medium small mb-1">End Date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm border-gray-300 rounded"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </>
            )}

            {(activeTab === "R2" || activeTab === "R5") && (
              <div className="col-md-3 col-sm-6">
                <label className="form-label text-dark fw-medium small mb-1">Expiry Bucket</label>
                <select
                  className="form-select form-select-sm border-gray-300 rounded"
                  value={expiryBucket}
                  onChange={(e) => setExpiryBucket(e.target.value)}
                >
                  <option value="">All Buckets</option>
                  <option value="Expired">Expired</option>
                  <option value="0-30">0-30 days</option>
                  <option value="31-60">31-60 days</option>
                  <option value="61-90">61-90 days</option>
                  <option value="90+">90+ days</option>
                </select>
              </div>
            )}

            {(activeTab === "R1" || activeTab === "R2" || activeTab === "R3") && (
              <div className="col-md-3 col-sm-6">
                <label className="form-label text-dark fw-medium small mb-1">Retailer Code</label>
                <input
                  type="text"
                  className="form-control form-control-sm border-gray-300 rounded"
                  placeholder="Enter Retailer Code"
                  value={retailerCode}
                  onChange={(e) => setRetailerCode(e.target.value)}
                />
              </div>
            )}

            {(activeTab === "R1" || activeTab === "R2" || activeTab === "R3" || activeTab === "R5") && (
              <>
                <div className="col-md-3 col-sm-6">
                  <label className="form-label text-dark fw-medium small mb-1">Product Code</label>
                  <input
                    type="text"
                    className="form-control form-control-sm border-gray-300 rounded"
                    placeholder="Enter Product Code"
                    value={productCode}
                    onChange={(e) => setProductCode(e.target.value)}
                  />
                </div>
                <div className="col-md-3 col-sm-6">
                  <label className="form-label text-dark fw-medium small mb-1">Batch Code</label>
                  <input
                    type="text"
                    className="form-control form-control-sm border-gray-300 rounded"
                    placeholder="Enter Batch Code"
                    value={batchCode}
                    onChange={(e) => setBatchCode(e.target.value)}
                  />
                </div>
              </>
            )}

            {(activeTab === "R1" || activeTab === "R4") && (
              <>
                <div className="col-md-3 col-sm-6">
                  <label className="form-label text-dark fw-medium small mb-1">State</label>
                  <input
                    type="text"
                    className="form-control form-control-sm border-gray-300 rounded"
                    placeholder="Enter State"
                    value={stateFilter}
                    onChange={(e) => setStateFilter(e.target.value)}
                  />
                </div>
                <div className="col-md-3 col-sm-6">
                  <label className="form-label text-dark fw-medium small mb-1">District</label>
                  <input
                    type="text"
                    className="form-control form-control-sm border-gray-300 rounded"
                    placeholder="Enter District"
                    value={districtFilter}
                    onChange={(e) => setDistrictFilter(e.target.value)}
                  />
                </div>
              </>
            )}

            <div className="col-12 d-flex gap-2 justify-content-end pt-2">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm px-4 fw-medium"
                onClick={handleResetFilter}
              >
                Reset
              </button>
              <button type="submit" className="btn btn-primary btn-sm px-4 fw-medium shadow-sm">
                Apply Filter
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Data Table */}
      <div className="card shadow-sm">
        <div className="card-body p-0">
          {isLoading ? (
            <div className="py-5">
              <CircleLoaderComponent />
            </div>
          ) : (
            <DataTableComponent
              columns={getColumns()}
              data={tableData}
              totalRows={totalRows}
              paginationDefaultPage={currentPage}
              paginationPerPage={limit}
              handlePageChange={(page) => setCurrentPage(page)}
              handlePerRowsChange={(newLimit) => setLimit(newLimit)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default IffcoReports;

